import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from "react-native";

import type { EventNode } from "@/types/event";

import { GText } from "@/components/g-text";
import {
  type ParsedPlace,
  PlacesAutocomplete,
} from "@/components/places-autocomplete";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { DurationPickerList } from "@/components/ui/duration-picker-list";
import { colors } from "@/constants/colors";
import { toEwkbHex } from "@/utils/event-helpers";
import { convertTextIntoMarkdown } from "@/utils/formatStrings";

// ── Types ──────────────────────────────────────────────────────────

export interface TicketDraft {
  currency: string;
  description: null | string;
  name: string;
  price_cents: number;
  quantity_total: null | number;
}

interface DateDraft {
  ends_at: null | string | undefined;
  starts_at: null | string | undefined;
}

interface LocationDraft {
  location_city: null | string | undefined;
  location_country: null | string | undefined;
  location_geo: null | string | undefined;
  location_name: null | string | undefined;
  location_neighborhood: null | string | undefined;
  location_place_id: null | string | undefined;
  location_postal_code: null | string | undefined;
  location_state: null | string | undefined;
  location_street: null | string | undefined;
}

interface SectionEditModalProps {
  event: EventNode;
  hasExistingTicket?: boolean;
  onClose: () => void;
  onEventChange: (patch: Partial<EventNode>) => void;
  onRemoveTicket?: () => void;
  onTicketChange: (patch: TicketDraft) => void;
  section: "date" | "description" | "location" | "ticket" | "title" | null;
}

// ── Main component ─────────────────────────────────────────────────

export function SectionEditModal({
  event,
  hasExistingTicket,
  onClose,
  onEventChange,
  onRemoveTicket,
  onTicketChange,
  section,
}: SectionEditModalProps) {
  const visible = section !== null;
  const ticketKind = event.event_ticket_kindCollection?.edges[0]?.node ?? null;

  // Section-specific draft states
  const [titleDraft, setTitleDraft] = useState(event.title ?? "");
  const [descDraft, setDescDraft] = useState(event.description ?? "");
  const [dateDraft, setDateDraft] = useState<DateDraft>({
    ends_at: event.ends_at,
    starts_at: event.starts_at,
  });
  const [locationDraft, setLocationDraft] = useState<LocationDraft>({
    location_city: event.location_city,
    location_country: event.location_country,
    location_geo: event.location_geo,
    location_name: event.location_name,
    location_neighborhood: event.location_neighborhood,
    location_place_id: event.location_place_id,
    location_postal_code: event.location_postal_code,
    location_state: event.location_state,
    location_street: event.location_street,
  });
  const [ticketDraft, setTicketDraft] = useState<TicketDraft>({
    currency: ticketKind?.currency ?? "usd",
    description: ticketKind?.description ?? null,
    name: ticketKind?.name ?? "General Admission",
    price_cents: ticketKind?.price_cents ?? 0,
    quantity_total: ticketKind?.quantity_total ?? 10,
  });

  // Reset drafts when modal opens
  const resetDrafts = useCallback(() => {
    if (!visible) return;
    setTitleDraft(event.title ?? "");
    setDescDraft(event.description ?? "");
    setDateDraft({ ends_at: event.ends_at, starts_at: event.starts_at });
    setLocationDraft({
      location_city: event.location_city,
      location_country: event.location_country,
      location_geo: event.location_geo,
      location_name: event.location_name,
      location_neighborhood: event.location_neighborhood,
      location_place_id: event.location_place_id,
      location_postal_code: event.location_postal_code,
      location_state: event.location_state,
      location_street: event.location_street,
    });
    const tk = event.event_ticket_kindCollection?.edges[0]?.node;
    setTicketDraft({
      currency: tk?.currency ?? "usd",
      description: tk?.description ?? null,
      name: tk?.name ?? "General Admission",
      price_cents: tk?.price_cents ?? 0,
      quantity_total: tk?.quantity_total ?? 10,
    });
  }, [visible, event]);

  useEffect(() => {
    resetDrafts();
  }, [resetDrafts]);

  const handleDone = () => {
    switch (section) {
      case "date": {
        let finalEndsAt = dateDraft.ends_at;
        if (dateDraft.starts_at && !finalEndsAt) {
          finalEndsAt = DateTime.fromISO(dateDraft.starts_at)
            .plus({ minutes: 120 })
            .toISO()!;
        }
        onEventChange({
          ends_at: finalEndsAt,
          starts_at: dateDraft.starts_at,
        });
        break;
      }
      case "description":
        onEventChange({
          description: descDraft
            ? convertTextIntoMarkdown(descDraft)
            : descDraft,
        });
        break;
      case "location":
        console.log("[SectionEditModal] handleDone location draft:", {
          allKeys: Object.keys(locationDraft),
          location_geo: locationDraft.location_geo,
          location_name: locationDraft.location_name,
        });
        onEventChange(locationDraft);
        break;
      case "ticket":
        onTicketChange(ticketDraft);
        break;
      case "title":
        onEventChange({ title: titleDraft });
        break;
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      animationType="fade"
      onRequestClose={handleDone}
      transparent
      visible={visible}
    >
      <Pressable onPress={handleDone} style={styles.backdrop}>
        <Pressable onPress={() => {}} style={styles.sheet}>
          <ScrollView
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            style={Platform.OS === "web" ? { overflow: "visible" } : undefined}
          >
            <View style={styles.buttonRow}>
              <Pressable onPress={handleCancel}>
                <GText style={styles.cancelText}>✕ cancel</GText>
              </Pressable>
              <GText style={styles.sectionTitle}>edit {section}</GText>
              <Pressable onPress={handleDone}>
                <GText style={styles.doneText}>done ✓</GText>
              </Pressable>
            </View>

            {section === "title" && (
              <TitleSection onChange={setTitleDraft} value={titleDraft} />
            )}
            {section === "description" && (
              <DescriptionSection onChange={setDescDraft} value={descDraft} />
            )}
            {section === "date" && (
              <DateTimeSection
                onChange={setDateDraft}
                timezone={event.timezone}
                value={dateDraft}
              />
            )}
            {section === "location" && (
              <LocationSection
                onChange={setLocationDraft}
                value={locationDraft}
              />
            )}
            {section === "ticket" && (
              <TicketSection
                hasExistingTicket={hasExistingTicket}
                onChange={setTicketDraft}
                onRemoveTicket={onRemoveTicket}
                value={ticketDraft}
              />
            )}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// ── Section components (alphabetical) ──────────────────────────────

function DateTimeSection({
  onChange,
  timezone,
  value,
}: {
  onChange: (v: DateDraft) => void;
  timezone: string;
  value: DateDraft;
}) {
  const durationMinutes = (() => {
    if (!value.starts_at || !value.ends_at) return null;
    const start = DateTime.fromISO(value.starts_at);
    const end = DateTime.fromISO(value.ends_at);
    if (!start.isValid || !end.isValid) return null;
    return Math.round(end.diff(start, "minutes").minutes);
  })();

  return (
    <>
      <DateTimePicker
        label="starts at"
        onChange={(v) => {
          const newStart = v;
          let newEnd = value.ends_at;
          if (newStart && durationMinutes) {
            newEnd = DateTime.fromISO(newStart)
              .plus({ minutes: durationMinutes })
              .toISO()!;
          }
          onChange({ ends_at: newEnd, starts_at: newStart });
        }}
        placeholder="Select start date & time"
        timezone={timezone}
        value={value.starts_at ?? ""}
      />
      <GText style={styles.label}>lasting</GText>
      <DurationPickerList
        onClear={() => onChange({ ...value, ends_at: null })}
        onSelectDuration={(minutes) => {
          if (!value.starts_at) return;
          const newEnd = DateTime.fromISO(value.starts_at)
            .plus({ minutes })
            .toISO()!;
          onChange({ ...value, ends_at: newEnd });
        }}
        optional
        selectedMinutes={durationMinutes}
      />
    </>
  );
}

function DescriptionSection({
  onChange,
  value,
}: {
  onChange: (v: string) => void;
  value: string;
}) {
  return (
    <Field
      multiline
      onChangeText={(t) => onChange(t.toLowerCase())}
      placeholder="add description"
      value={value}
    />
  );
}

function Field({
  label,
  multiline,
  onChangeText,
  placeholder,
  value,
}: {
  label?: string;
  multiline?: boolean;
  onChangeText: (t: string) => void;
  placeholder?: string;
  value: string;
}) {
  return (
    <View style={styles.field}>
      {label && <GText style={styles.label}>{label}</GText>}
      <TextInput
        multiline={multiline}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={colors.placeholder}
        style={[styles.input, multiline && styles.inputMultiline]}
        value={value}
      />
    </View>
  );
}

function LocationSection({
  onChange,
  value,
}: {
  onChange: (v: LocationDraft) => void;
  value: LocationDraft;
}) {
  const handlePlaceSelected = (place: ParsedPlace) => {
    const locationGeo =
      place.location_lat && place.location_lng
        ? toEwkbHex(
            parseFloat(place.location_lng),
            parseFloat(place.location_lat),
          )
        : null;
    console.log("[LocationSection] handlePlaceSelected:", {
      computedGeo: locationGeo,
      incomingLat: place.location_lat,
      incomingLng: place.location_lng,
    });
    onChange({
      location_city: place.location_city,
      location_country: place.location_country,
      location_geo: locationGeo,
      location_name: place.location_name,
      location_neighborhood: place.location_neighborhood,
      location_place_id: place.location_place_id,
      location_postal_code: place.location_postal_code,
      location_state: place.location_state,
      location_street: place.location_street,
    });
  };

  return (
    <>
      {Platform.OS === "web" ? (
        <PlacesAutocomplete
          defaultValue={
            [
              value.location_name,
              value.location_street,
              value.location_city,
              value.location_state,
            ]
              .filter(Boolean)
              .join(", ") || undefined
          }
          onPlaceSelected={handlePlaceSelected}
        />
      ) : (
        <>
          <Field
            label="street"
            onChangeText={(t) => onChange({ ...value, location_street: t })}
            placeholder="123 Main St"
            value={value.location_street ?? ""}
          />
          <Field
            label="city"
            onChangeText={(t) => onChange({ ...value, location_city: t })}
            placeholder="City"
            value={value.location_city ?? ""}
          />
          <Field
            label="state"
            onChangeText={(t) => onChange({ ...value, location_state: t })}
            placeholder="State"
            value={value.location_state ?? ""}
          />
          <Field
            label="postal code"
            onChangeText={(t) =>
              onChange({ ...value, location_postal_code: t })
            }
            placeholder="12345"
            value={value.location_postal_code ?? ""}
          />
          <Field
            label="country"
            onChangeText={(t) => onChange({ ...value, location_country: t })}
            placeholder="US"
            value={value.location_country ?? ""}
          />
        </>
      )}
    </>
  );
}

function TicketSection({
  hasExistingTicket,
  onChange,
  onRemoveTicket,
  value,
}: {
  hasExistingTicket?: boolean;
  onChange: (v: TicketDraft) => void;
  onRemoveTicket?: () => void;
  value: TicketDraft;
}) {
  const initial =
    value.price_cents === 0
      ? ""
      : (value.price_cents / 100).toFixed(2).replace(/\.00$/, "");
  const [dollarInput, setDollarInput] = useState(initial);

  return (
    <>
      <View style={styles.ticketRow}>
        <View style={styles.ticketField}>
          <TextInput
            inputMode="decimal"
            keyboardType="decimal-pad"
            onChangeText={(t) => {
              setDollarInput(t);
              const cents = Math.round((parseFloat(t) || 0) * 100);
              onChange({ ...value, price_cents: cents });
            }}
            placeholder="5.00"
            placeholderTextColor={colors.placeholder}
            style={styles.input}
            value={dollarInput}
          />
          <GText style={styles.label}>price</GText>
        </View>
        <View style={styles.ticketField}>
          <TextInput
            keyboardType="number-pad"
            onChangeText={(t) => {
              const cleaned = t.replace(/[^0-9]/g, "");
              onChange({
                ...value,
                quantity_total: cleaned ? parseInt(cleaned, 10) : null,
              });
            }}
            placeholder="7"
            placeholderTextColor={colors.placeholder}
            style={styles.input}
            value={
              value.quantity_total != null ? String(value.quantity_total) : ""
            }
          />
          <GText style={styles.label}>quantity</GText>
        </View>
      </View>
      {hasExistingTicket && onRemoveTicket ? (
        <Pressable onPress={onRemoveTicket} style={styles.makeEventFreeButton}>
          <GText style={styles.makeEventFreeText}>make event free</GText>
        </Pressable>
      ) : null}
    </>
  );
}

function TitleSection({
  onChange,
  value,
}: {
  onChange: (v: string) => void;
  value: string;
}) {
  return (
    <Field
      onChangeText={(t) => onChange(t.toLowerCase())}
      placeholder="title"
      value={value}
    />
  );
}

const styles = StyleSheet.create({
  backdrop: {
    alignItems: "center",
    backgroundColor: colors.darkOverlay,
    flex: 1,
    justifyContent: "center",
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
  },
  cancelText: {
    color: colors.midGray,
    fontSize: 16,
    fontWeight: "600",
  },
  doneText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: "600",
  },
  field: {
    marginBottom: 16,
  },
  input: {
    backgroundColor: colors.cardBg,
    borderColor: colors.divider,
    borderRadius: 12,
    borderWidth: 1,
    color: colors.white,
    fontSize: 16,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  inputMultiline: {
    minHeight: 120,
    textAlignVertical: "top",
  },
  label: {
    color: colors.light,
    fontSize: 13,
    marginBottom: 6,
  },
  makeEventFreeButton: {
    alignItems: "center" as const,
    marginTop: 16,
    paddingVertical: 12,
  },
  makeEventFreeText: {
    color: colors.midGray,
    fontSize: 14,
    fontWeight: "600" as const,
  },
  sectionTitle: {
    color: colors.light,
    fontSize: 14,
    fontWeight: "700",
    letterSpacing: 1,
    marginBottom: 12,
  },
  sheet: {
    backgroundColor: colors.sheet,
    borderColor: colors.cardBg,
    borderRadius: 20,
    borderWidth: 1,
    marginHorizontal: 24,
    maxHeight: "80%",
    maxWidth: 420,
    overflow: "visible",
    paddingHorizontal: 20,
    paddingVertical: 24,
    width: "100%",
  },
  ticketField: {
    flex: 1,
  },
  ticketRow: {
    flexDirection: "row",
    gap: 12,
  },
});
